/**
 * Phone-number helpers shared between the funnel (client) and the
 * Twilio service (server). Pure string operations — no Twilio SDK or
 * Node-only imports so this module is safe to pull into the client
 * bundle.
 */

/**
 * Format a phone number to E.164. Accepts a few common German shapes
 * and falls back to assuming `+49` when no country code prefix is given.
 *
 * MUST stay in lockstep with `public.normalize_phone_e164()`
 * (supabase/migrations/20260728032348_users_phone_e164.sql, amended by
 * 20260824115857_phone_plus_trunk_and_contact_uniqueness.sql), which generates the
 * `users.phone_e164` column that phone lookups match against. Any input the two
 * normalize differently becomes an account the login path cannot find — which is
 * how phone login used to mint duplicate accounts. `phone.test.ts` pins the
 * shared vectors.
 *
 * Note the first step strips everything except digits and "+", so the two
 * implementations cannot disagree about which characters count as whitespace
 * (JS `trim()` strips tabs and NBSP; Postgres `btrim()` strips only spaces).
 */
export function formatPhoneNumber(phone: string): string {
  let cleaned = phone.replace(/[^0-9+]/g, "");
  const digits = (s: string) => s.replace(/\D/g, "");
  if (digits(cleaned) === "") return "";

  // "+0…" is never a country code — E.164 codes start 1-9. It is a typo for a
  // national number whose trunk "0" was typed with a "+" in front ("+0160…"),
  // or an international prefix wearing both hats ("+0049…"). Drop the "+" and
  // let the national branches below decide; that turns "+0160…" into
  // "+49160…" instead of the un-diallable "+0160…" we used to store.
  if (cleaned.startsWith("+0")) cleaned = cleaned.slice(1);

  let e164: string;
  // A leading "+" or "00" carries its own country code; keep it.
  if (cleaned.startsWith("+")) e164 = "+" + digits(cleaned);
  else if (cleaned.startsWith("00")) e164 = "+" + digits(cleaned.slice(2));
  // Otherwise assume Germany.
  else if (cleaned.startsWith("0")) e164 = "+49" + digits(cleaned.slice(1));
  else e164 = "+49" + digits(cleaned);

  // Drop the German trunk prefix from "+49 (0) 151 …". Scoped to +49 on
  // purpose: in other country codes a leading 0 is part of the subscriber
  // number (Italian landlines, for example).
  return e164.startsWith("+490") ? "+49" + e164.slice(4) : e164;
}

/**
 * Expected national-significant-number length per country calling code,
 * as `[min, max]` digit counts (the digits AFTER the calling code).
 *
 * Why this exists: the old check was `/^\+[1-9]\d{7,14}$/` — it trusted any
 * leading "+" as a country code and then only counted total digits. A German
 * 0160 mobile typed with "+" instead of "0" ("+1605354724") therefore parsed as
 * a NANP number and passed, and we stored an un-diallable number for a real
 * lead (2026-08-13). NANP is exactly 10 national digits; nine is not a phone
 * number in any of its 20 countries.
 *
 * Scope: every calling code offered by `PhoneInput`'s country selector, which is
 * the only way the funnel can produce one. An unlisted code falls back to the
 * generic length rule below, so this table can only ever make validation
 * STRICTER — adding a country here is a refinement, never a prerequisite.
 */
const NSN_LENGTHS: Record<string, readonly [number, number]> = {
  "1": [10, 10], // NANP — US, CA and 18 more; always exactly 10
  "7": [10, 10], // RU, KZ
  "20": [9, 10], // EG
  "27": [9, 9], // ZA
  "30": [10, 10], // GR
  "31": [9, 9], // NL
  "32": [8, 9], // BE
  "33": [9, 9], // FR
  "34": [9, 9], // ES
  "36": [8, 9], // HU
  "39": [6, 11], // IT (leading 0 is part of the subscriber number)
  "40": [9, 9], // RO
  "41": [9, 9], // CH
  "43": [4, 13], // AT — deliberately wide, Austrian NSNs vary a lot
  "44": [7, 10], // GB
  "45": [8, 8], // DK
  "46": [7, 13], // SE
  "47": [8, 8], // NO
  "48": [9, 9], // PL
  "49": [4, 13], // DE — wide on purpose; see DE_MOBILE_NSN below
  "51": [8, 9], // PE
  "52": [10, 10], // MX
  "54": [10, 11], // AR
  "55": [10, 11], // BR
  "56": [9, 9], // CL
  "57": [10, 10], // CO
  "60": [7, 10], // MY
  "61": [9, 9], // AU
  "62": [8, 12], // ID
  "63": [10, 10], // PH
  "64": [8, 10], // NZ
  "65": [8, 8], // SG
  "66": [9, 9], // TH
  "81": [9, 10], // JP
  "82": [8, 11], // KR
  "84": [9, 10], // VN
  "86": [8, 11], // CN
  "90": [10, 10], // TR
  "91": [10, 10], // IN
  "234": [8, 10], // NG
  "254": [9, 9], // KE
  "351": [9, 9], // PT
  "352": [4, 11], // LU
  "353": [7, 10], // IE
  "354": [7, 9], // IS
  "355": [8, 9], // AL
  "356": [8, 8], // MT
  "357": [8, 8], // CY
  "358": [5, 12], // FI
  "359": [8, 9], // BG
  "370": [8, 8], // LT
  "371": [8, 8], // LV
  "372": [7, 10], // EE
  "373": [8, 8], // MD
  "375": [9, 9], // BY
  "376": [6, 9], // AD
  "377": [8, 9], // MC
  "378": [6, 10], // SM
  "380": [9, 9], // UA
  "381": [8, 9], // RS
  "382": [8, 8], // ME
  "383": [8, 9], // XK
  "385": [8, 9], // HR
  "386": [8, 8], // SI
  "387": [8, 8], // BA
  "389": [8, 8], // MK
  "420": [9, 9], // CZ
  "421": [9, 9], // SK
  "423": [7, 9], // LI
  "852": [8, 8], // HK
  "886": [8, 9], // TW
  "966": [9, 9], // SA
  "971": [8, 9], // AE
  "972": [8, 9], // IL
};

/**
 * German mobile blocks (015x/016x/017x) carry a fixed national length: 10 or 11
 * digits including the leading block digit. The generic `+49` range above has to
 * stay wide for landlines (Germany has 3-digit local numbers), so mobile is
 * pinned separately — that is what catches a mistyped 0170 number with a digit
 * too few or too many, the single most common shape of un-diallable lead.
 */
const DE_MOBILE_NSN: readonly [number, number] = [10, 11];

/** Longest-match calling-code lookup — "+423…" must beat "+42…"/"+4…". */
const CALLING_CODES_BY_LENGTH = Object.keys(NSN_LENGTHS).sort((a, b) => b.length - a.length);

/**
 * Returns true if `phone` parses to a valid E.164 number for its country.
 *
 * Two-stage: the E.164 shape (leading "+", non-zero country code, 8–15 digits
 * total) and then the national-number length expected for the calling code. An
 * unrecognised calling code stops after stage one, so this is never LOOSER than
 * the shape-only check it replaced.
 *
 * Server- and client-side callers share it, so the funnel-side check, the OTP
 * send check and the admin lead-create guard stay in sync. There is deliberately
 * no SQL counterpart — `normalize_phone_e164()` normalizes, it does not judge.
 */
export function isValidPhoneNumber(phone: string): boolean {
  const formatted = formatPhoneNumber(phone);
  if (!/^\+[1-9]\d{7,14}$/.test(formatted)) return false;

  const digits = formatted.slice(1);
  const code = CALLING_CODES_BY_LENGTH.find((c) => digits.startsWith(c));
  if (!code) return true; // unknown country — shape check is all we have

  const nsn = digits.slice(code.length);
  const [min, max] = code === "49" && /^1[567]/.test(nsn) ? DE_MOBILE_NSN : NSN_LENGTHS[code];

  return nsn.length >= min && nsn.length <= max;
}

/**
 * The user-facing German wording for a rejected phone number. One definition,
 * because there were three: the funnel's client check, `UserService`'s
 * server-side guard and the admin lead-create guard each carried their own
 * string — and `PATCH /api/contact-info` carried none at all, so the same
 * number the funnel refused was accepted without complaint from the post-submit
 * Ergänzen workbench.
 */
export const PHONE_VALIDATION_MESSAGES = {
  missing: "Bitte geben Sie eine Telefonnummer ein.",
  invalid: "Bitte geben Sie eine gültige Telefonnummer ein (z. B. +49 170 1234567).",
} as const;

export type PhoneValidationFailure = keyof typeof PHONE_VALIDATION_MESSAGES;

export type PhoneValidationResult =
  | { ok: true; e164: string }
  | { ok: false; reason: PhoneValidationFailure; message: string };

/**
 * The single entry point for "is this phone input acceptable, and what do we
 * tell the user if not". Every surface that accepts a phone number — funnel
 * client, funnel API, Ergänzen autosave, admin lead create — goes through this
 * so a change to the rules or the wording lands everywhere at once.
 *
 * `required` is the only thing that legitimately differs between callers: the
 * contact step demands a number, the enrichment endpoints only care that a
 * number, if supplied, is diallable. An absent optional number is `ok` with an
 * empty `e164` — nothing to reject, and nothing to write.
 *
 * `e164` is the normalized form for callers that store or dial it. Note the
 * funnel deliberately persists the RAW string to `users.phone`; the canonical
 * form lives in the generated `users.phone_e164` column.
 */
export function validatePhoneInput(
  value: string | null | undefined,
  options: { required?: boolean } = {}
): PhoneValidationResult {
  const raw = value?.trim() ?? "";

  if (!raw) {
    return options.required
      ? {
          ok: false,
          reason: "missing",
          message: PHONE_VALIDATION_MESSAGES.missing,
        }
      : { ok: true, e164: "" };
  }

  if (!isValidPhoneNumber(raw)) {
    return {
      ok: false,
      reason: "invalid",
      message: PHONE_VALIDATION_MESSAGES.invalid,
    };
  }

  return { ok: true, e164: formatPhoneNumber(raw) };
}

/**
 * Structured error codes the OTP-send endpoints return alongside their German
 * `error` string. The code is what a client branches on; the string is what a
 * client that does NOT know the code still shows the user. Both are always
 * present, so no surface degrades to a generic fallback.
 */
export const PHONE_OTP_ERROR_CODES = {
  landlineNotSupported: "landline_not_supported",
} as const;

export type PhoneOtpErrorCode = (typeof PHONE_OTP_ERROR_CODES)[keyof typeof PHONE_OTP_ERROR_CODES];

/**
 * The neutral wording, used by every OTP-send endpoint. A surface that also
 * stores the number (the Ergänzen ContactSection) APPENDS a sentence about
 * keeping it as a callback number - that claim is only true where the number is
 * actually persisted, so it does not live here. Appends, literally: the
 * component composes on this constant rather than restating it, because a
 * retyped copy is how `PHONE_VALIDATION_MESSAGES` above came to have three
 * divergent versions.
 *
 * The wording says "wir erkennen sie als Festnetznummer", not "sie ist eine
 * Festnetznummer", and it is careful for two reasons the first draft was not:
 *
 * 1. `formatPhoneNumber` assumes +49 for a number typed without a country
 *    code, so an Austrian mobile entered as "0664 1234567" becomes a German
 *    number that libphonenumber types FIXED_LINE. Telling that lead their
 *    mobile IS a landline is false and unactionable — hence the sentence about
 *    the country code, which is the only thing that can actually fix it.
 * 2. The same code is emitted by the post-hoc Twilio branch for numbers the
 *    classifier returned "unknown" for (TOLL_FREE, VOIP, an unlisted range),
 *    which are not landlines either.
 */
export const PHONE_OTP_ERROR_MESSAGES: Record<PhoneOtpErrorCode, string> = {
  landline_not_supported:
    "An diese Nummer können wir keine SMS senden – wir erkennen sie als Festnetznummer. Bitte geben Sie für die Bestätigung eine Mobilnummer an. Bei einer ausländischen Nummer wählen Sie diese bitte mit Ländervorwahl, z. B. +43 664 1234567.",
};

/**
 * The last four digits of a number, for telling its owner WHICH number is meant
 * ("endet auf 5678") — in the profile and in security mails. Never the whole
 * number: a mail can be forwarded, and the screen can be over a shoulder.
 *
 * A number with fewer than eight digits yields "", because four of them would
 * be most of it. For log lines use `maskPhoneForLog`.
 */
export function phoneEnding(phone: string | null | undefined): string {
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length < 8 ? "" : digits.slice(-4);
}

/**
 * A phone number reduced to what a log line legitimately needs: the country
 * code and the last two digits. Enough to correlate two log lines or spot a
 * wrong country, not enough to identify or dial the lead.
 *
 * Why this exists: `[TWILIO DEBUG] Sending verification to: "+49..."` printed
 * whole lead phone numbers into Vercel's production logs, which are retained
 * for 30 days (found in the 2026-09-07 recap sweep).
 *
 * A calling code this module does not know is rendered `+?` rather than
 * guessed. The first version emitted `digits.slice(0, 2)`, which turned
 * `+8801712345678` into `+88...78` — "+88" is not a country, and a mask that
 * INVENTS a plausible country is worse than one that admits it does not know.
 *
 * Numbers too short to hide anything (fewer than three digits beyond the
 * calling code) lose the tail entirely rather than being echoed in full.
 */
export function maskPhoneForLog(phone: string | null | undefined): string {
  const e164 = formatPhoneNumber(phone ?? "");
  if (!e164) return "(empty)";

  const digits = e164.slice(1);
  const code = CALLING_CODES_BY_LENGTH.find((c) => digits.startsWith(c));
  const label = code ?? "?";
  const rest = code ? digits.slice(code.length) : digits;

  return rest.length < 3 ? `+${label}...` : `+${label}...${rest.slice(-2)}`;
}

/**
 * Phone numbers do not only reach the logs through our own interpolation -
 * Twilio echoes the `To` number back inside its error messages ("'+49...'
 * number cannot be a landline"), so `console.error("...", error)` leaked the
 * lead on exactly the requests that failed. This masks the dialable numbers in
 * a free-text string.
 *
 * An explicit international prefix (`+` or `00`) is REQUIRED, and that
 * restriction is the whole design. The first version matched any run of seven
 * or more digit-ish characters, which is not a phone-number test at all: it
 * rewrote `/Services/VA0123456789abcdef.../` into `VA+49...89abcdef`,
 * `connect ETIMEDOUT 34.239.194.108:443` into `+49...08:443`, and every UUID
 * and ISO timestamp it met — destroying the Twilio service id and request id
 * that are the first things support asks for, in the one log line that has
 * them. Every number Twilio echoes back is the E.164 value we sent it, so the
 * `+` is always there when it matters.
 *
 * A number typed in national form inside someone else's prose is NOT caught
 * here. `describeTwilioError` closes that gap for the OTP path by masking the
 * specific number the request was for, whatever shape it appears in.
 */
export function redactPhoneNumbers(text: string): string {
  return text.replace(/(?:\+|\b00)\d[\d\s().-]{5,}\d/g, (match) => maskPhoneForLog(match));
}

/**
 * Mask every appearance of ONE known number in a string, in any of the shapes
 * a third party might echo it back: the E.164 form, the same digits without
 * the `+`, and the national form with a trunk `0`.
 *
 * Needed because `redactPhoneNumbers` only recognises numbers carrying an
 * explicit international prefix. When we already know which number the request
 * was about, we do not have to recognise it — we can look for it.
 */
export function redactKnownPhoneNumber(text: string, phone: string): string {
  const e164 = formatPhoneNumber(phone);
  if (!e164 || e164.length < 5) return text;

  const digits = e164.slice(1);
  const masked = maskPhoneForLog(e164);

  const variants = [digits, `00${digits}`];
  // The national form only exists for numbers whose calling code we know.
  const code = CALLING_CODES_BY_LENGTH.find((c) => digits.startsWith(c));
  if (code) variants.push(`0${digits.slice(code.length)}`);

  // Match the digits across whatever grouping separators the third party used:
  // "030 12345678" and "030-12345678" are the same number as "03012345678",
  // and an exact string comparison sees three different strings.
  const gap = "[\\s().-]*";
  return variants
    .sort((a, b) => b.length - a.length)
    .reduce(
      (acc, v) => acc.replace(new RegExp(`(?:\\+${gap})?${v.split("").join(gap)}`, "g"), masked),
      text
    );
}

/**
 * The national-format pattern `redactPhoneNumbers` deliberately does not use.
 *
 * A dialable national number starts with a trunk `0` or an opening paren.
 * Requiring that — and refusing a match that continues a word or a decimal — is
 * what keeps it away from the identifiers the international pattern was
 * tightened to protect. Verified against the shipping helper: ISO timestamps,
 * UUIDs, grouped amounts and version strings are all left alone, while
 * `030/123456` and `0171/2345678` are masked.
 *
 * `/` is a separator because it is how Germans actually write a number, and the
 * separator class is horizontal whitespace only so digits on different lines
 * cannot fuse into one fabricated number.
 */
const NATIONAL_PHONE_IN_PROSE = /(?<![\w.])(?:0\d|\(\d)[\d \t().\/-]{4,}\d/g;

/**
 * Mask dialable numbers in HUMAN free text — a chat message, a note — where
 * there is no known number to look for.
 *
 * `redactPhoneNumbers` requires an explicit `+` or `00` and says so: every
 * number Twilio echoes back carries one, and the restriction is what stops it
 * eating service ids and timestamps. That is right for machine strings and
 * wrong for prose, where a German writes `0171/2345678` and no international
 * prefix ever appears. `redactKnownPhoneNumber` cannot close this gap either —
 * it needs a number to look for, and an inbound Teams message has none.
 *
 * Use this ONLY on text a person typed. On a machine string full of ids, use
 * `redactPhoneNumbers`, whose narrower pattern is the point.
 */
export function redactPhoneNumbersInProse(text: string): string {
  return redactPhoneNumbers(text).replace(NATIONAL_PHONE_IN_PROSE, (match) =>
    maskPhoneForLog(match)
  );
}
